import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import PhoneInput from "@/components/base/PhoneInput";
import RegistrationsClosedNotice from "@/components/feature/RegistrationsClosedNotice";
import { marcar } from "@/lib/rastreio";

interface RegistrationFormProps {
  isSoldOut?: boolean;
  registrationsClosed?: boolean;
}

export default function RegistrationForm({ isSoldOut = false, registrationsClosed = false }: RegistrationFormProps) {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [whatsapp, setWhatsapp] = useState("");
  const [city, setCity] = useState("");
  const [profession, setProfession] = useState("");
  const [instagram, setInstagram] = useState("");
  const [referralSource, setReferralSource] = useState("");
  const [referralName, setReferralName] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Extract slug and event from URL
  const slug = window.location.pathname.split("/proximo-evento/")[1] || "";
  const [eventTitle, setEventTitle] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!name.trim() || !email.trim()) {
      setError("Nome e email são obrigatórios.");
      return;
    }

    if (!whatsapp.trim()) {
      setError("WhatsApp é obrigatório.");
      return;
    }

    if (!city.trim()) {
      setError("Cidade é obrigatória.");
      return;
    }

    if (!profession.trim()) {
      setError("Profissão / Área de atuação é obrigatória.");
      return;
    }

    if (!referralSource) {
      setError("Informe onde você ouviu falar sobre o evento.");
      return;
    }

    if (referralSource === "indicacao" && !referralName.trim()) {
      setError("Informe o nome da amiga ou membro que indicou o evento.");
      return;
    }

    setSubmitting(true);

    const whatsappDigits = whatsapp.replace(/\D/g, "");

    try {
      // 1. Garante que a integrante existe (cria como pendente se for nova).
      const { data: memberId, error: memberErr } = await supabase.rpc(
        "ensure_member_for_registration",
        {
          p_name: name.trim(),
          p_email: email.trim(),
          p_whatsapp: whatsapp,
          p_country_code: countryCode,
          p_city: city.trim(),
          p_profession: profession.trim(),
        },
      );

      if (memberErr || !memberId) {
        console.error("Erro ao processar cadastro:", memberErr);
        setError("Erro ao processar seu cadastro. Tente novamente.");
        setSubmitting(false);
        return;
      }

      // 2. Find the event by slug
      const { data: event, error: eventErr } = await supabase
        .from("upcoming_events")
        .select("id, title, event_datetime, location, registrations_closed")
        .eq("unique_link_slug", slug)
        .eq("active", true)
        .single();

      if (eventErr || !event) {
        console.error("Evento nao encontrado:", eventErr);
        setError("Evento não encontrado ou não está mais disponível.");
        setSubmitting(false);
        return;
      }

      if (event.registrations_closed) {
        setError("As inscrições para este evento já foram encerradas.");
        setSubmitting(false);
        return;
      }

      setEventTitle(event.title);

      // 3. Create event registration
      const { error: regErr } = await supabase
        .from("event_registrations")
        .insert({
          event_id: event.id,
          member_id: memberId,
          name: name.trim(),
          email: email.trim().toLowerCase(),
          whatsapp: whatsappDigits,
          country_code: countryCode,
          city: city.trim(),
          profession: profession.trim(),
          instagram: instagram.trim(),
          referral_source: referralSource,
          referral_name: referralSource === "indicacao" ? referralName.trim() : null,
          status: "pending",
        });

      if (regErr) {
        console.error("Erro ao criar inscricao:", regErr);
        setError("Erro ao registrar inscrição. Tente novamente.");
        setSubmitting(false);
        return;
      }

      // 4. Send notification emails
      const eventDateStr = `${new Date(event.event_datetime).toLocaleDateString("pt-BR", {
        weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "America/New_York",
      })} às ${new Date(event.event_datetime).toLocaleTimeString("en-US", {
        hour: "numeric", minute: "2-digit", hour12: true, timeZone: "America/New_York",
      })}`;

      await supabase.functions.invoke("notify-event-registration", {
        body: {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          eventTitle: event.title,
          eventDate: eventDateStr,
          eventLocation: event.location || "",
          whatsapp: `${countryCode} ${whatsappDigits}`,
          city: city.trim(),
          profession: profession.trim(),
          instagram: instagram.trim(),
          referralSource,
          referralName: referralSource === "indicacao" ? referralName.trim() : "",
          baseUrl: window.location.origin,
        },
      });

      // WhatsApp: "inscrição recebida" para a inscrita e aviso para a Ana (a função envia uma vez só)
      const zap = await supabase.functions.invoke("whatsapp-evento", {
        body: { acao: "recebida", eventSlug: slug, email: email.trim().toLowerCase() },
      });
      if (zap.error) console.error("WhatsApp da inscrição não enviado:", zap.error);

      marcar("CompleteRegistration");
      setSuccess(true);
    } catch (err: any) {
      console.error("Erro:", err);
      setError("Erro inesperado. Tente novamente mais tarde.");
    }

    setSubmitting(false);
  }

  if (registrationsClosed) {
    return (
      <section className="py-16 md:py-24 bg-beige">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-6 md:p-10">
            <RegistrationsClosedNotice />
          </div>
        </div>
      </section>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white rounded-2xl p-8 md:p-10 text-center">
          {isSoldOut ? (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/15 flex items-center justify-center">
                <i className="ri-user-star-line text-2xl text-primary" />
              </div>
              <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                Inscrição na lista de espera recebida!
              </h2>
              <p className="font-body text-sm text-brown/70 leading-relaxed mb-2">
                {name}, sua inscrição para o evento <strong>{eventTitle}</strong> foi recebida com sucesso!
              </p>
              <p className="font-body text-sm text-brown/60 leading-relaxed mb-6">
                Como todas as vagas já foram reservadas, você entrou na <strong>lista de espera</strong>. Se uma vaga abrir, a gente chama pela ordem de chegada, pelo WhatsApp.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <i className="ri-check-line text-2xl text-green-600" />
              </div>
              <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                Inscrição recebida!
              </h2>
              <p className="font-body text-sm text-brown/70 leading-relaxed mb-2">
                {name}, sua inscrição para o evento <strong>{eventTitle}</strong> foi recebida com sucesso e está em análise.
              </p>
              <p className="font-body text-sm text-brown/60 leading-relaxed mb-6">
                Em breve você recebe pelo WhatsApp e por e-mail a pré-reserva da sua vaga, com os dados do Zelle. A vaga fica confirmada depois do pagamento.
              </p>
            </>
          )}
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap cursor-pointer"
          >
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }

  return (
    <section id="inscricao" className="py-16 md:py-24 bg-beige scroll-mt-20">
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl p-6 md:p-10">
          <div className="text-center mb-8">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
              Inscreva-se
            </p>
            <h2 className="font-display text-xl md:text-2xl text-brown mb-2">
              Quero participar
            </h2>
            <p className="font-body text-sm text-brown/60">
              Preencha o formulário abaixo para se inscrever no evento.
            </p>
          </div>

          {error && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-red-500 text-sm" />
              </div>
              <p className="font-body text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Nome completo <span className="text-primary">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="Seu nome completo"
              />
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                E-mail <span className="text-primary">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="seu@email.com"
              />
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                WhatsApp <span className="text-primary">*</span>
              </label>
              <PhoneInput
                countryCode={countryCode}
                whatsapp={whatsapp}
                onChange={({ countryCode: cc, whatsapp: w }) => {
                  setCountryCode(cc);
                  setWhatsapp(w);
                }}
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Cidade <span className="text-primary">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="Ex: Coconut Creek, FL"
              />
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Profissão / Área de atuação <span className="text-primary">*</span>
              </label>
              <input
                type="text"
                name="profession"
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="Ex: Designer, Advogada, Empreendedora..."
              />
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Instagram (opcional)
              </label>
              <input
                type="text"
                name="instagram"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="@seuinstagram"
              />
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Onde você ouviu falar sobre o evento? <span className="text-primary">*</span>
              </label>
              <select
                name="referral_source"
                value={referralSource}
                onChange={(e) => setReferralSource(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2378654b' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "16px",
                }}
              >
                <option value="">Selecione uma opção</option>
                <option value="instagram">Instagram</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="indicacao">Indicação de uma amiga ou membro</option>
              </select>
            </div>

            {referralSource === "indicacao" && (
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Nome da amiga ou membro que indicou <span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  name="referral_name"
                  value={referralName}
                  onChange={(e) => setReferralName(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="Nome de quem te indicou"
                />
              </div>
            )}

            <div className="px-4 py-3 rounded-xl bg-secondary/10 border border-secondary/20">
              <p className="font-body text-xs text-brown/70 leading-relaxed text-center">
                Após concluir seu cadastro, você receberá um e-mail de confirmação. Caso tenha problemas para se inscrever, chame no WhatsApp +1 (561) 939-9706.
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-3.5 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Enviando...
                </span>
              ) : (
                "Enviar inscrição"
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}